/**
 * Stock instrument samples, normalized into the shape the randomizer needs.
 *
 * Pure and free of network/React so pool handling is unit-testable: the API
 * response is untrusted shape, and a malformed row must never break the
 * randomize button.
 */

/** MIDI note numbers for note names, used to read the [C] tag in a filename. */
const NOTE_OFFSETS = Object.freeze({
  C: 0, 'C#': 1, DB: 1, D: 2, 'D#': 3, EB: 3, E: 4, F: 5,
  'F#': 6, GB: 6, G: 7, 'G#': 8, AB: 8, A: 9, 'A#': 10, BB: 10, B: 11
});

export const DEFAULT_ROOT_NOTE = 60; // C4

/**
 * Parse a musical key such as "C", "F#", "Bb3" into a MIDI note number.
 * Bare note names carry no octave, so they resolve against C4 — matching how
 * the collection tags every file as [C].
 */
export function rootNoteFromKey(key, fallback = DEFAULT_ROOT_NOTE) {
  const text = String(key || '').trim().toUpperCase().replace(/[[\]()]/g, '');
  if (!text) return fallback;
  const match = text.match(/^([A-G])([#B]?)(-?\d+)?$/);
  if (!match) return fallback;
  const [, letter, accidental, octave] = match;
  const offset = NOTE_OFFSETS[`${letter}${accidental}`];
  if (offset === undefined) return fallback;
  // MIDI 60 = C4, so octave n starts at (n + 1) * 12.
  const octaveNumber = octave === undefined ? 4 : Number(octave);
  const note = ((octaveNumber + 1) * 12) + offset;
  return note >= 0 && note <= 127 ? note : fallback;
}

/**
 * Normalize one stock-library row. Returns null when the row cannot produce a
 * playable zone, so callers can filter without inspecting shapes themselves.
 */
export function normalizeSampleEntry(row) {
  if (!row || typeof row !== 'object') return null;
  const id = String(row.id || '').trim();
  const url = String(row.url || row.previewUrl || '').trim();
  // Without an id there is nothing to fetch access for, and without either an
  // id or a url the zone can never load audio.
  if (!id && !url) return null;
  return {
    id: id || url,
    name: String(row.name || 'Sample').trim().slice(0, 160),
    category: String(row.category || '').trim(),
    rootNote: typeof row.rootNote === 'number' && Number.isInteger(row.rootNote) && row.rootNote >= 0 && row.rootNote <= 127
      ? row.rootNote : rootNoteFromKey(row.musicalKey),
    url,
    duration: Number.isFinite(Number(row.duration)) ? Number(row.duration) : null,
    version: Math.max(1, Math.round(Number(row.version) || 1))
  };
}

/** Normalize an API payload into a pool, dropping unusable rows. */
export function buildSamplePool(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map(normalizeSampleEntry).filter(Boolean);
}

/**
 * Build a sample zone covering the full keyboard from one pool entry.
 * The collection is one file per instrument rooted at C, so a single
 * full-range zone is the correct mapping — pitch tracks from the root.
 */
export function sampleZoneFromEntry(entry, zoneId = 'zone-1') {
  return {
    id: zoneId,
    bufferId: entry.id,
    url: entry.url,
    ...(entry.storageKey ? { storageKey: entry.storageKey } : {}),
    name: entry.name,
    rootNote: entry.rootNote,
    loNote: 0,
    hiNote: 127,
    loVelocity: 0,
    hiVelocity: 1,
    sourceType: entry.sourceType || (entry.id && !entry.url?.startsWith('http') ? 'stock' : ''),
    assetId: entry.assetId || entry.id,
    assetVersion: entry.assetVersion || entry.version || 1
  };
}

/**
 * Flatten a GET /api/library/stock response into flat rows for one assetType.
 * The payload nests type → genre → category → files; the pool only needs the
 * files. Any level may be missing or malformed, so every step is guarded.
 */
export function flattenStockSections(payload, assetType = 'instrument') {
  const sections = Array.isArray(payload?.sections) ? payload.sections : [];
  const section = sections.find((entry) => entry?.type === assetType);
  if (!section) return [];
  const genres = Array.isArray(section.genres) ? section.genres : [];
  return genres.flatMap((genre) => {
    const categories = Array.isArray(genre?.categories) ? genre.categories : [];
    return categories.flatMap((category) => {
      const files = Array.isArray(category?.files) ? category.files : [];
      // Prefer the row's own category, falling back to the group it sits in.
      return files.map((file) => ({ ...file, category: file?.category || category?.label || '' }));
    });
  });
}

/** Build a pool directly from a stock-library payload. */
export function samplePoolFromStockResponse(payload, assetType = 'instrument') {
  return buildSamplePool(flattenStockSections(payload, assetType));
}
